
import * as React from 'react';
import * as stylesImport from './DetailsList.scss';

import {
  BaseComponent,
  KeyCodes,
  assign,
  autobind,
  css,
  elementContains,
  getRTLSafeKeyCode,
  IRenderFunction
} from '../../Utilities';
import {
  CheckboxVisibility,
  ColumnActionsMode,
  ConstrainMode,
  DetailsListLayoutMode,
  IColumn,
  IDetailsList,
  IDetailsListProps,
} from '../DetailsList/DetailsList.types';
import { DetailsHeader, IDetailsHeader, SelectAllVisibility, IDetailsHeaderProps } from '../DetailsList/DetailsHeader';
import { DetailsRow, IDetailsRowProps } from '../DetailsList/DetailsRow';
import { FocusZone, FocusZoneDirection } from '../../FocusZone';
import {
  IObjectWithKey,
  ISelection,
  Selection,
  SelectionMode,
  SelectionZone,
} from '../../utilities/selection/index';

import { DragDropHelper } from '../../utilities/dragdrop/DragDropHelper';
import { GroupedList } from '../../GroupedList';
import { List, IListProps } from '../../List';
import { withViewport } from '../../utilities/decorators/withViewport';
import { GetGroupCount } from '../../utilities/groupedList/GroupedListUtility';

const styles: any = stylesImport;

export interface IDetailsListState {
  focusedItemIndex: number;
  lastWidth?: number;
  lastSelectionMode?: SelectionMode;
  adjustedColumns?: IColumn[];
  isCollapsed?: boolean;
  isSizing?: boolean;
  isDropping?: boolean;
  isSomeGroupExpanded?: boolean;
}

const MIN_COLUMN_WIDTH = 100; // this is the global min width
const CHECKBOX_WIDTH = 40;
const GROUP_EXPAND_WIDTH = 36;
const DEFAULT_INNER_PADDING = 16;
const ISPADDED_WIDTH = 24;

const DEFAULT_RENDERED_WINDOWS_AHEAD = 2;
const DEFAULT_RENDERED_WINDOWS_BEHIND = 2;

@withViewport
export class DetailsList extends BaseComponent<IDetailsListProps, IDetailsListState> implements IDetailsList {
  public static defaultProps = {
    layoutMode: DetailsListLayoutMode.justified,
    selectionMode: SelectionMode.multiple,
    constrainMode: ConstrainMode.horizontalConstrained,
    checkboxVisibility: CheckboxVisibility.onHover,
    isHeaderVisible: true
  };

  // References
  // tslint:disable-next-line:no-unused-variable
  private _root: HTMLElement;
  private _header: IDetailsHeader;
  private _groupedList: GroupedList;
  private _list: List;
  private _focusZone: FocusZone;
  private _selectionZone: SelectionZone;

  private _selection: ISelection;
  private _activeRows: { [key: string]: DetailsRow };
  private _dragDropHelper: DragDropHelper | null;
  private _initialFocusedIndex: number | undefined;
  private _pendingForceUpdate: boolean;

  private _columnOverrides: {
    [key: string]: IColumn;
  };

  constructor(props: IDetailsListProps) {
    super(props);

    this._activeRows = {};
    this._columnOverrides = {};
    this._onColumnIsSizingChanged = this._onColumnIsSizingChanged.bind(this);
    this._onColumnResized = this._onColumnResized.bind(this);
    this._onColumnAutoResized = this._onColumnAutoResized.bind(this);
    this._onRowDidMount = this._onRowDidMount.bind(this);
    this._onRowWillUnmount = this._onRowWillUnmount.bind(this);
    this._onToggleCollapse = this._onToggleCollapse.bind(this);
    this._onActiveRowChanged = this._onActiveRowChanged.bind(this);
    this._onBlur = this._onBlur.bind(this);
    this._onHeaderKeyDown = this._onHeaderKeyDown.bind(this);
    this._onContentKeyDown = this._onContentKeyDown.bind(this);
    this._onRenderCell = this._onRenderCell.bind(this);
    this._onGroupExpandStateChanged = this._onGroupExpandStateChanged.bind(this);

    this.state = {
      focusedItemIndex: -1,
      lastWidth: 0,
      adjustedColumns: this._getAdjustedColumns(props),
      isSizing: false,
      isDropping: false,
      isCollapsed: props.groupProps && props.groupProps.isAllGroupsCollapsed,
      isSomeGroupExpanded: props.groupProps && !props.groupProps.isAllGroupsCollapsed
    };

    this._selection = props.selection || new Selection({ onSelectionChanged: undefined, getKey: props.getKey });
    this._selection.setItems(props.items as IObjectWithKey[], false);
    this._dragDropHelper = props.dragDropEvents ? new DragDropHelper({
      selection: this._selection,
      minimumPixelsForDrag: props.minimumPixelsForDrag
    }) : null;
    this._initialFocusedIndex = props.initialFocusedIndex;
  }

  public scrollToIndex(index: number, measureItem?: (itemIndex: number) => number): void {
    this._list && this._list.scrollToIndex(index, measureItem);
    this._groupedList && this._groupedList.scrollToIndex(index, measureItem);
  }

  public componentWillUnmount() {
    if (this._dragDropHelper) {
      this._dragDropHelper.dispose();
    }
  }

  public componentDidUpdate(prevProps: any, prevState: any) {
    if (this._initialFocusedIndex !== undefined) {
      const item = this.props.items[this._initialFocusedIndex];
      if (item) {
        const itemKey = this._getItemKey(item, this._initialFocusedIndex);
        const row = this._activeRows[itemKey];
        if (row) {
          this._setFocusToRowIfPending(row);
        }
      }
    }

    if (
      this.props.items !== prevProps.items &&
      this.props.items.length > 0 &&
      this.state.focusedItemIndex !== -1 &&
      !elementContains(this._root, document.activeElement as HTMLElement, false)
    ) {
      // Item set has changed and previously-focused item is gone.
      // Set focus to item at index of previously-focused item if it is in range,
      // else set focus to the last item.
      const index = this.state.focusedItemIndex < this.props.items.length ?
        this.state.focusedItemIndex :
        this.props.items.length - 1;
      const item = this.props.items[index];
      const itemKey = this._getItemKey(item, this.state.focusedItemIndex);
      const row = this._activeRows[itemKey];
      if (row) {
        this._setFocusToRow(row);
      } else {
        this._initialFocusedIndex = index;
      }
    }

    if (this.props.onDidUpdate) {
      this.props.onDidUpdate(this);
    }
  }

  public componentWillReceiveProps(newProps: IDetailsListProps) {
    const {
      checkboxVisibility,
      items,
      setKey,
      selectionMode,
      columns,
      viewport
    } = this.props;
    const shouldResetSelection = (newProps.setKey !== setKey) || newProps.setKey === undefined;
    let shouldForceUpdates = false;

    if (newProps.layoutMode !== this.props.layoutMode) {
      shouldForceUpdates = true;
    }

    if (shouldResetSelection) {
      this._initialFocusedIndex = newProps.initialFocusedIndex;
    }

    if (newProps.items !== items) {
      this._selection.setItems(newProps.items, shouldResetSelection);
    }

    if (
      newProps.checkboxVisibility !== checkboxVisibility ||
      newProps.columns !== columns ||
      newProps.viewport!.width !== viewport!.width
    ) {
      shouldForceUpdates = true;
    }

    this._adjustColumns(newProps, true);

    if (newProps.selectionMode !== selectionMode) {
      shouldForceUpdates = true;
    }

    if (shouldForceUpdates) {
      this._pendingForceUpdate = true;
    }
  }

  public componentWillUpdate(): void {
    if (this._pendingForceUpdate) {
      this._forceListUpdates();
    }
  }

  public render() {
    const {
      ariaLabelForListHeader,
      ariaLabelForSelectAllCheckbox,
      ariaLabelForSelectionColumn,
      className,
      checkboxVisibility,
      compact,
      constrainMode,
      dragDropEvents,
      groups,
      groupProps,
      items,
      isHeaderVisible,
      layoutMode,
      onItemInvoked,
      onItemContextMenu,
      onColumnHeaderClick,
      onColumnHeaderContextMenu,
      selectionMode,
      selectionPreservedOnEmptyClick,
      ariaLabel,
      ariaLabelForGrid,
      rowElementEventMap,
      shouldApplyApplicationRole = false,
      getKey,
      listProps,
      usePageCache,
      onShouldVirtualize
    } = this.props;
    const {
      adjustedColumns,
      isCollapsed,
      isSizing,
      isSomeGroupExpanded
    } = this.state;
    const {
      _selection: selection,
      _dragDropHelper: dragDropHelper
    } = this;
    const groupNestingDepth = this._getGroupNestingDepth();
    const additionalListProps: IListProps = {
      renderedWindowsAhead: isSizing ? 0 : DEFAULT_RENDERED_WINDOWS_AHEAD,
      renderedWindowsBehind: isSizing ? 0 : DEFAULT_RENDERED_WINDOWS_BEHIND,
      getKey,
      ...listProps
    };
    let selectAllVisibility = SelectAllVisibility.none; // for SelectionMode.none
    if (selectionMode === SelectionMode.single) {
      selectAllVisibility = SelectAllVisibility.hidden;
    }
    if (selectionMode === SelectionMode.multiple) {
      // if isCollapsedGroupSelectVisible is false, disable select all when the list has all collapsed groups
      let isCollapsedGroupSelectVisible = groupProps && groupProps.headerProps && groupProps.headerProps.isCollapsedGroupSelectVisible;
      if (isCollapsedGroupSelectVisible === undefined) {
        isCollapsedGroupSelectVisible = true;
      }
      const isSelectAllVisible = isCollapsedGroupSelectVisible || !groups || isSomeGroupExpanded;
      selectAllVisibility = isSelectAllVisible ? SelectAllVisibility.visible : SelectAllVisibility.hidden;
    }

    if (checkboxVisibility === CheckboxVisibility.hidden) {
      selectAllVisibility = SelectAllVisibility.none;
    }

    const {
      onRenderDetailsHeader = this._onRenderDetailsHeader
    } = this.props;

    const rowCount = (isHeaderVisible ? 1 : 0) + GetGroupCount(groups) + (items ? items.length : 0);

    return (
      // If shouldApplyApplicationRole is true, role application will be applied to make arrow keys work
      // with JAWS.
      <div
        ref={ this._resolveRef('_root') }
        className={ css(
          'ms-DetailsList',
          styles.root,
          className,
          (layoutMode === DetailsListLayoutMode.fixedColumns) && 'is-fixed',
          (constrainMode === ConstrainMode.horizontalConstrained) && ('is-horizontalConstrained ' + styles.rootIsHorizontalConstrained),
          !!compact && ('ms-DetailsList--Compact ' + styles.rootCompact)
        ) }
        data-automationid='DetailsList'
        data-is-scrollable='false'
        aria-label={ ariaLabel }
        { ...(shouldApplyApplicationRole ? { role: 'application' } : {}) }
      >
        <div
          role='grid'
          aria-label={ ariaLabelForGrid }
          aria-rowcount={ rowCount }
          aria-colcount={ (selectAllVisibility !== SelectAllVisibility.none ? 1 : 0) + (adjustedColumns ? adjustedColumns.length : 0) }
          aria-readonly='true'
        >
          <div onKeyDown={ this._onHeaderKeyDown } role='presentation'>
            { isHeaderVisible && onRenderDetailsHeader({
              componentRef: this._resolveRef('_header'),
              selectionMode: selectionMode!,
              layoutMode: layoutMode!,
              selection: selection,
              columns: adjustedColumns as IColumn[],
              onColumnClick: onColumnHeaderClick,
              onColumnContextMenu: onColumnHeaderContextMenu,
              onColumnResized: this._onColumnResized,
              onColumnIsSizingChanged: this._onColumnIsSizingChanged,
              onColumnAutoResized: this._onColumnAutoResized,
              groupNestingDepth: groupNestingDepth,
              isAllCollapsed: isCollapsed,
              onToggleCollapseAll: this._onToggleCollapse,
              ariaLabel: ariaLabelForListHeader,
              ariaLabelForSelectAllCheckbox: ariaLabelForSelectAllCheckbox,
              ariaLabelForSelectionColumn: ariaLabelForSelectionColumn,
              selectAllVisibility: selectAllVisibility,
              collapseAllVisibility: groupProps && groupProps.collapseAllVisibility
            }, this._onRenderDetailsHeader) }
          </div>
          <div onKeyDown={ this._onContentKeyDown } role='presentation'>
            <FocusZone
              ref={ this._resolveRef('_focusZone') }
              className={ styles.focusZone }
              direction={ FocusZoneDirection.vertical }
              isInnerZoneKeystroke={ isRightArrow }
              onActiveElementChanged={ this._onActiveRowChanged }
              onBlur={ this._onBlur }
            >
              <SelectionZone
                ref={ this._resolveRef('_selectionZone') }
                selection={ selection }
                selectionPreservedOnEmptyClick={ selectionPreservedOnEmptyClick }
                selectionMode={ selectionMode }
                onItemInvoked={ onItemInvoked }
                onItemContextMenu={ onItemContextMenu }
                enterModalOnTouch={ this.props.enterModalSelectionOnTouch }
              >
                { groups ? (
                  <GroupedList
                    ref={ this._resolveRef('_groupedList') }
                    groups={ groups }
                    groupProps={ groupProps }
                    items={ items }
                    onRenderCell={ this._onRenderCell }
                    selection={ selection }
                    selectionMode={ selectionMode }
                    dragDropEvents={ dragDropEvents }
                    dragDropHelper={ dragDropHelper as DragDropHelper }
                    eventsToRegister={ rowElementEventMap }
                    listProps={ additionalListProps }
                    onGroupExpandStateChanged={ this._onGroupExpandStateChanged }
                    usePageCache={ usePageCache }
                    onShouldVirtualize={ onShouldVirtualize }
                  />
                ) : (
                    <List
                      ref={ this._resolveRef('_list') }
                      role='presentation'
                      items={ items }
                      onRenderCell={ this._onRenderListCell(0) }
                      usePageCache={ usePageCache }
                      onShouldVirtualize={ onShouldVirtualize }
                      { ...additionalListProps }
                    />
                  )
                }
              </SelectionZone>
            </FocusZone>
          </div>
        </div>
      </div>
    );
  }

  public forceUpdate() {
    super.forceUpdate();
    this._forceListUpdates();
  }

  @autobind
  protected _onRenderRow(props: IDetailsRowProps, defaultRender?: any) {
    return <DetailsRow { ...props } />;
  }

  @autobind
  private _onRenderDetailsHeader(detailsHeaderProps: IDetailsHeaderProps, defaultRender?: IRenderFunction<IDetailsHeaderProps>) {
    return <DetailsHeader { ...detailsHeaderProps } />;
  }

  @autobind
  private _onRenderListCell(nestingDepth: number)
    : (item: any, itemIndex: number) => React.ReactNode {
    return (item: any, itemIndex: number): React.ReactNode => {
      return this._onRenderCell(nestingDepth, item, itemIndex as number);
    };
  }

  private _onRenderCell(nestingDepth: number, item: any, index: number): React.ReactNode {
    const {
      compact,
      dragDropEvents,
      rowElementEventMap: eventsToRegister,
      onRenderMissingItem,
      onRenderItemColumn,
      onRenderRow = this._onRenderRow,
      selectionMode,
      viewport,
      checkboxVisibility,
      getRowAriaLabel,
      getRowAriaDescribedBy,
      checkButtonAriaLabel,
      checkboxCellClassName,
      groupProps
    } = this.props;
    const collapseAllVisibility = groupProps && groupProps.collapseAllVisibility;
    const selection = this._selection;
    const dragDropHelper = this._dragDropHelper;
    const {
      adjustedColumns: columns
    } = this.state;

    if (!item) {
      if (onRenderMissingItem) {
        return onRenderMissingItem(index);
      }

      return null;
    }

    return onRenderRow({
      item: item,
      itemIndex: index,
      compact: compact,
      columns: columns as IColumn[],
      groupNestingDepth: nestingDepth,
      selectionMode: selectionMode!,
      selection: selection,
      onDidMount: this._onRowDidMount,
      onWillUnmount: this._onRowWillUnmount,
      onRenderItemColumn: onRenderItemColumn,
      eventsToRegister: eventsToRegister,
      dragDropEvents: dragDropEvents,
      dragDropHelper: dragDropHelper!,
      viewport: viewport,
      checkboxVisibility: checkboxVisibility,
      collapseAllVisibility: collapseAllVisibility,
      getRowAriaLabel: getRowAriaLabel,
      getRowAriaDescribedBy: getRowAriaDescribedBy,
      checkButtonAriaLabel: checkButtonAriaLabel,
      checkboxCellClassName: checkboxCellClassName,
    }, this._onRenderRow);
  }

  private _onGroupExpandStateChanged(isSomeGroupExpanded: boolean) {
    this.setState({ isSomeGroupExpanded: isSomeGroupExpanded });
  }

  private _onColumnIsSizingChanged(column: IColumn, isSizing: boolean) {
    this.setState({ isSizing: isSizing });
  }

  private _onHeaderKeyDown(ev: React.KeyboardEvent<HTMLElement>) {
    if (ev.which === KeyCodes.down) {
      if (this._focusZone && this._focusZone.focus()) {
        ev.preventDefault();
        ev.stopPropagation();
      }
    }
  }

  private _onContentKeyDown(ev: React.KeyboardEvent<HTMLElement>) {
    if (ev.which === KeyCodes.up && !ev.altKey) {
      if (this._header && this._header.focus()) {
        ev.preventDefault();
        ev.stopPropagation();
      }
    }
  }

  private _getGroupNestingDepth(): number {
    const { groups } = this.props;
    let level = 0;
    let groupsInLevel = groups;

    while (groupsInLevel && groupsInLevel.length > 0) {
      level++;
      groupsInLevel = groupsInLevel[0].children;
    }

    return level;
  }

  private _onRowDidMount(row: DetailsRow) {
    const { item, itemIndex } = row.props;
    const itemKey = this._getItemKey(item, itemIndex);
    this._activeRows[itemKey] = row; // this is used for column auto resize

    this._setFocusToRowIfPending(row);

    const { onRowDidMount } = this.props;
    if (onRowDidMount) {
      onRowDidMount(item, itemIndex);
    }
  }

  private _setFocusToRowIfPending(row: DetailsRow) {
    const { itemIndex } = row.props;
    if (this._initialFocusedIndex !== undefined && itemIndex === this._initialFocusedIndex) {
      this._setFocusToRow(row);
      delete this._initialFocusedIndex;
    }
  }

  private _setFocusToRow(row: DetailsRow) {
    if (this._selectionZone) {
      this._selectionZone.ignoreNextFocus();
    }
    this._async.setTimeout(() => {
      row.focus();
    }, 0);
  }

  private _onRowWillUnmount(row: DetailsRow) {
    const { onRowWillUnmount } = this.props;

    const { item, itemIndex } = row.props;
    const itemKey = this._getItemKey(item, itemIndex);
    delete this._activeRows[itemKey];

    if (onRowWillUnmount) {
      onRowWillUnmount(item, itemIndex);
    }
  }

  private _onToggleCollapse(collapsed: boolean) {
    this.setState({
      isCollapsed: collapsed
    });
    if (this._groupedList) {
      this._groupedList.toggleCollapseAll(collapsed);
    }
  }

  private _forceListUpdates() {
    this._pendingForceUpdate = false;

    if (this._groupedList) {
      this._groupedList.forceUpdate();
    }
    if (this._list) {
      this._list.forceUpdate();
    }
  }

  private _adjustColumns(newProps: IDetailsListProps, forceUpdate?: boolean, resizingColumnIndex?: number) {
    const adjustedColumns = this._getAdjustedColumns(newProps, forceUpdate, resizingColumnIndex);
    const { width: viewportWidth } = this.props.viewport!;

    if (adjustedColumns) {
      this.setState({
        adjustedColumns: adjustedColumns,
        lastWidth: viewportWidth,
      });
    }
  }

  /** Returns adjusted columns, given the viewport size and layout mode. */
  private _getAdjustedColumns(newProps: IDetailsListProps, forceUpdate?: boolean, resizingColumnIndex?: number): IColumn[] | undefined {
    const { items: newItems, layoutMode, selectionMode } = newProps;
    let { columns: newColumns } = newProps;
    let { width: viewportWidth } = newProps.viewport!;

    const columns = this.props ? this.props.columns : [];
    const lastWidth = this.state ? this.state.lastWidth : -1;
    const lastSelectionMode = this.state ? this.state.lastSelectionMode : undefined;

    if (viewportWidth !== undefined) {
      if (!forceUpdate &&
        lastWidth === viewportWidth &&
        lastSelectionMode === selectionMode &&
        (!columns || newColumns === columns)) {
        return undefined;
      }
    } else {
      viewportWidth = this.props.viewport!.width;
    }

    newColumns = newColumns || buildColumns(newItems, true);

    let adjustedColumns: IColumn[];

    if (layoutMode === DetailsListLayoutMode.fixedColumns) {
      adjustedColumns = this._getFixedColumns(newColumns);

      // Preserve adjusted column calculated widths.
      adjustedColumns.forEach(column => {
        this._rememberCalculatedWidth(column, column.calculatedWidth!);
      });
    } else {
      if (resizingColumnIndex !== undefined) {
        adjustedColumns = this._getJustifiedColumnsAfterResize(newColumns, viewportWidth, newProps, resizingColumnIndex);
      } else {
        adjustedColumns = this._getJustifiedColumns(newColumns, viewportWidth, newProps, 0);
      }

      adjustedColumns.forEach(column => {
        this._getColumnOverride(column.key).currentWidth = column.calculatedWidth;
      });
    }

    return adjustedColumns;
  }

  /** Builds a set of columns based on the given columns mixed with the current overrides. */
  private _getFixedColumns(newColumns: IColumn[]) {
    return newColumns.map(column => {
      const newColumn = assign({}, column, this._columnOverrides[column.key]);

      if (!newColumn.calculatedWidth) {
        newColumn.calculatedWidth = newColumn.maxWidth || newColumn.minWidth || MIN_COLUMN_WIDTH;
      }

      return newColumn;
    });
  }

  private _getJustifiedColumnsAfterResize(newColumns: IColumn[], viewportWidth: number, props: IDetailsListProps, resizingColumnIndex: number): IColumn[] {
    const fixedColumns = newColumns.slice(0, resizingColumnIndex);
    fixedColumns.forEach(column =>
      column.calculatedWidth = this._getColumnOverride(column.key).currentWidth);

    const fixedWidth = fixedColumns.reduce((total, column, i) => total + getPaddedWidth(column, i === 0), 0);

    const remainingColumns = newColumns.slice(resizingColumnIndex);
    const remainingWidth = viewportWidth - fixedWidth;

    return [
      ...fixedColumns,
      ...this._getJustifiedColumns(remainingColumns, remainingWidth, props, resizingColumnIndex),
    ];
  }

  /** Builds a set of columns to fix within the viewport width. */
  private _getJustifiedColumns(newColumns: IColumn[], viewportWidth: number, props: IDetailsListProps, firstIndex: number) {
    const {
      selectionMode,
      checkboxVisibility,
      groups
    } = props;
    const outerPadding = DEFAULT_INNER_PADDING;
    const rowCheckWidth = (selectionMode !== SelectionMode.none && checkboxVisibility !== CheckboxVisibility.hidden) ? CHECKBOX_WIDTH : 0;
    const groupExpandWidth = groups ? GROUP_EXPAND_WIDTH : 0;
    let totalWidth = 0; // offset because we have one less inner padding.
    const availableWidth = viewportWidth - (outerPadding + rowCheckWidth + groupExpandWidth);
    const adjustedColumns: IColumn[] = newColumns.map((column, i) => {
    const newColumn = assign(
        {},
        column,
        {
          calculatedWidth: column.minWidth || MIN_COLUMN_WIDTH
        },
        this._columnOverrides[column.key]);

      const isFirst = i + firstIndex === 0;
      totalWidth += getPaddedWidth(newColumn, isFirst);

      return newColumn;
    });

    let lastIndex = adjustedColumns.length - 1;

    // Shrink or remove collapsable columns.
    while (lastIndex > 0 && totalWidth > availableWidth) {
      const column = adjustedColumns[lastIndex];

      const minWidth = column.minWidth || MIN_COLUMN_WIDTH;
      const overflowWidth = totalWidth - availableWidth;

      if (column.calculatedWidth! - minWidth >= overflowWidth || !column.isCollapsable) {
        column.calculatedWidth = Math.max(column.calculatedWidth! - overflowWidth, minWidth);
        totalWidth = availableWidth;
      } else {
        totalWidth -= getPaddedWidth(column, false);
        adjustedColumns.splice(lastIndex, 1);
      }
      lastIndex--;
    }

    // Then expand columns starting at the beginning, until we've filled the width.
    for (let i = 0; i < adjustedColumns.length && totalWidth < availableWidth; i++) {
      const column = adjustedColumns[i];
      const isLast = i === (adjustedColumns.length - 1);
      const overrides = this._columnOverrides[column.key];
      if (overrides && overrides.calculatedWidth && !isLast) {
        continue;
      }

      const spaceLeft = availableWidth - totalWidth;
      let increment: number;
      if (isLast) {
        increment = spaceLeft;
      } else {
        const maxWidth = column.maxWidth;
        const minWidth = column.minWidth || maxWidth || MIN_COLUMN_WIDTH;
        increment = maxWidth ? Math.min(spaceLeft, maxWidth - minWidth) : spaceLeft;
      }

      column.calculatedWidth = (column.calculatedWidth as number) + increment;
      totalWidth += increment;
    }

    return adjustedColumns;
  }

  private _onColumnResized(resizingColumn: IColumn, newWidth: number, resizingColumnIndex: number) {
    const newCalculatedWidth = Math.max(resizingColumn.minWidth || MIN_COLUMN_WIDTH, newWidth);
    if (this.props.onColumnResize) {
      this.props.onColumnResize(resizingColumn, newCalculatedWidth, resizingColumnIndex);
    }

    this._rememberCalculatedWidth(resizingColumn, newCalculatedWidth);

    this._adjustColumns(this.props, true, resizingColumnIndex);
    this._forceListUpdates();
  }

  private _rememberCalculatedWidth(column: IColumn, newCalculatedWidth: number) {
    const overrides = this._getColumnOverride(column.key);
    overrides.calculatedWidth = newCalculatedWidth;
    overrides.currentWidth = newCalculatedWidth;
  }

  private _getColumnOverride(key: string): IColumn {
    return this._columnOverrides[key] = this._columnOverrides[key] || {} as IColumn;
  }

  /**
   * Callback function when double clicked on the details header column resizer
   * which will measure the column cells of all the active rows and resize the
   * column to the max cell width.
   *
   * @private
   * @param {IColumn} column (double clicked column definition)
   * @param {number} columnIndex (double clicked column index)
   * @todo min width 100 should be changed to const value and should be consistent with the value used on _onSizerMove method in DetailsHeader
   */
  private _onColumnAutoResized(column: IColumn, columnIndex: number) {
    let max = 0;
    let count = 0;
    const totalCount = Object.keys(this._activeRows).length;

    for (const key in this._activeRows) {
      if (this._activeRows.hasOwnProperty(key)) {
        const currentRow = this._activeRows[key];
        currentRow.measureCell(columnIndex, (width: number) => {
          max = Math.max(max, width);
          count++;
          if (count === totalCount) {
            this._onColumnResized(column, max, columnIndex);
          }
        });
      }
    }
  }

  /**
   * Call back function when an element in FocusZone becomes active. It will transalate it into item
   * and call onActiveItemChanged callback if specified.
   *
   * @private
   * @param {el} row element that became active in Focus Zone
   * @param {ev} focus event from Focus Zone
   */
  private _onActiveRowChanged(el?: HTMLElement, ev?: React.FocusEvent<HTMLElement>) {
    const { items, onActiveItemChanged } = this.props;

    if (!el) {
      return;
    }
    const index = Number(el.getAttribute('data-item-index'));
    if (index >= 0) {
      if (onActiveItemChanged) {
        onActiveItemChanged(items[index], index, ev);
      }
      this.setState({
        focusedItemIndex: index
      });
    }
  }

  private _onBlur(event: React.FocusEvent<HTMLElement>) {
    this.setState({
      focusedItemIndex: -1
    });
  }

  private _getItemKey(item: any, itemIndex: number): string | number {
    const { getKey } = this.props;

    let itemKey: string | number | undefined = undefined;
    if (item) {
      itemKey = item.key;
    }

    if (getKey) {
      itemKey = getKey(item, itemIndex);
    }

    if (!itemKey) {
      itemKey = itemIndex;
    }

    return itemKey;
  }
}

export function buildColumns(
  items: any[],
  canResizeColumns?: boolean,
  onColumnClick?: (ev: React.MouseEvent<HTMLElement>, column: IColumn) => any,
  sortedColumnKey?: string,
  isSortedDescending?: boolean,
  groupedColumnKey?: string,
  isMultiline?: boolean) {
  const columns: IColumn[] = [];

  if (items && items.length) {
    const firstItem = items[0];
    let isFirstColumn = true;

    for (const propName in firstItem) {
      if (firstItem.hasOwnProperty(propName)) {
        columns.push({
          key: propName,
          name: propName,
          fieldName: propName,
          minWidth: MIN_COLUMN_WIDTH,
          maxWidth: 300,
          isCollapsable: !!columns.length,
          isMultiline: (isMultiline === undefined) ? false : isMultiline,
          isSorted: sortedColumnKey === propName,
          isSortedDescending: !!isSortedDescending,
          isRowHeader: false,
          columnActionsMode: ColumnActionsMode.clickable,
          isResizable: canResizeColumns,
          onColumnClick: onColumnClick,
          isGrouped: groupedColumnKey === propName
        });

        isFirstColumn = false;
      }
    }
  }

  return columns;
}

function isRightArrow(event: React.KeyboardEvent<HTMLElement>) {
  return event.which === getRTLSafeKeyCode(KeyCodes.right);
}

function getPaddedWidth(column: IColumn, isFirst: boolean): number {
  return column.calculatedWidth! + (isFirst ? 0 : DEFAULT_INNER_PADDING) + (column.isPadded ? ISPADDED_WIDTH : 0);
}
